/**
 * 要件定義アサインの「この成果物は作らない」の宣言欄（決めすぎないゲート）。
 *
 * 要件定義は 4 成果物が揃うまで終われない作りで、「要らない」という状態がどこにも無かった
 * ——決める必要が無い案件（技術対応だけの案件・仕様を起こし直す必要が無いバグ票）でも
 * 埋めることになる。**構成を決めるのはアサインのとき、決める人は PMO とリーダー**で、
 * その口がここ。
 *
 * **理由を必ず書かせる。** チェックしただけでは送信できない（API も空の理由を弾く）。
 * 「作らない」は後から見て分からなくなる判断なので、記録の無い省略を作らせない。
 *
 * **初期値は AI の仮判断**（`suggest-requirement-scope.ts`）で、この欄は案件 1 件ぶんを人が
 * 直すためのもの。選択肢を作るのは AI、決めるのは PMO とリーダー——要件定義で「選択肢まで
 * 含めて考えるのは作成者、PL は判断するだけ」としたのと同じ分け方。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 要件定義アサインで「作らない成果物」を理由つきで選ばせる入力欄
 * @graph-connects none
 */

import type { ReactNode } from 'react';
import { Checkbox } from '@cortex/web-design-system/components/ui/Checkbox';
import { Input } from '@cortex/web-design-system/components/ui/Input';
import {
  ASSIGN_SKIPPABLE_ARTIFACTS,
  ASSIGN_SKIPPABLE_ARTIFACT_LABELS,
  ASSIGN_SKIP_REASON_MAX,
  type AssignSkippableArtifact,
  type AssignSkippedArtifact,
} from '../../../types/project-assign.js';

/** @graph-connects none */
export interface RequirementScopeFieldsProps {
  /** 今の宣言（チェック済みの成果物とその理由）。 */
  value: readonly AssignSkippedArtifact[];
  /** 宣言が変わったときに呼ぶ。 */
  onChange: (next: AssignSkippedArtifact[]) => void;
  /** 送信中は触らせない。 */
  disabled: boolean;
}

/**
 * 理由が空のまま宣言された成果物があるか（送信ボタンの活性判定に使う）。
 *
 * 画面側で止めるのは、送ってから行ごとのエラーで返されるより先に気づかせるため。
 * 弾く正本は API 側（`checkRequirementScope`）。
 * @graph-connects none
 */
export function hasEmptySkipReason(skipped: readonly AssignSkippedArtifact[]): boolean {
  return skipped.some((entry) => entry.reason.trim() === '');
}

/**
 * 作らない成果物の選択欄。
 * @graph-connects none
 */
export function RequirementScopeFields({
  value,
  onChange,
  disabled,
}: RequirementScopeFieldsProps): ReactNode {
  const reasonOf = (artifact: AssignSkippableArtifact): string | null =>
    value.find((entry) => entry.artifact === artifact)?.reason ?? null;

  // チェックを外したら理由も消す（外して付け直したときに前の理由が残っていると、
  // 書き換えないまま送ってしまう）。付けるときは `ASSIGN_SKIPPABLE_ARTIFACTS` の順に並べ直して、
  // チェックした順ではなく工程順で読めるようにする。
  const toggle = (artifact: AssignSkippableArtifact, checked: boolean) => {
    const kept = value.filter((entry) => entry.artifact !== artifact);
    const next = checked ? [...kept, { artifact, reason: '' }] : kept;
    onChange(
      ASSIGN_SKIPPABLE_ARTIFACTS.flatMap((key) => next.filter((entry) => entry.artifact === key)),
    );
  };

  const setReason = (artifact: AssignSkippableArtifact, reason: string) => {
    onChange(value.map((entry) => (entry.artifact === artifact ? { ...entry, reason } : entry)));
  };

  return (
    <div className="space-y-2 rounded-md border border-[var(--color-border)] px-4 py-3">
      <div className="space-y-0.5">
        <p className="text-xs font-semibold text-[var(--color-text-primary)]">
          この案件で作らない成果物（任意）
        </p>
        <p className="text-xs text-[var(--color-text-muted)]">
          チェックした成果物は子チケットに担当者を立てず、PI Lab も催促しません。理由は必須です。
          初期値は<strong className="font-semibold">AI の仮判断</strong>
          で、決めるのは実行するあなたです。
        </p>
      </div>
      <ul className="space-y-1.5">
        {ASSIGN_SKIPPABLE_ARTIFACTS.map((artifact) => {
          const reason = reasonOf(artifact);
          return (
            <li key={artifact} className="space-y-1">
              <label className="flex items-center gap-2 text-xs text-[var(--color-text-primary)]">
                <Checkbox
                  checked={reason !== null}
                  disabled={disabled}
                  onChange={(e) => {
                    toggle(artifact, e.target.checked);
                  }}
                />
                {ASSIGN_SKIPPABLE_ARTIFACT_LABELS[artifact]}
              </label>
              {reason !== null && (
                <Input
                  size="sm"
                  fullWidth
                  value={reason}
                  maxLength={ASSIGN_SKIP_REASON_MAX}
                  disabled={disabled}
                  placeholder="なぜ作らないか（例: 認証基盤の設定対応で、利用者に見える業務が変わらない）"
                  error={reason.trim() === '' ? '理由を書いてください' : undefined}
                  onChange={(e) => {
                    setReason(artifact, e.target.value);
                  }}
                />
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
